import store from 'angular-storage';
import crypto from 'sjcl';
import ngCookies from 'angular-cookies';

export default angular.module('eventix.common.services.auth', [ngCookies, store])
    /**
     * @ngdoc object
     * @name eventix.common.services.OAuthClientId
     * @description
     * Default OAuth client credentials; `client_id` and `client_secret`
     */
    .value('OAuthClientId', {
        client_id: OAUTH_ID,
        client_secret: OAUTH_SECRET
    })
    /**
     * @ngdoc object
     * @name eventix.common.services.OAuthTokens
     * @description
     * Dependency-less token storage
     */
    .value('OAuthTokens', {
        password: null,
        credentials: null
    })

    /**
     * @ngdoc service
     * @name eventix.common.services.Auth
     * @description
     * Handle application authorization, i.e. registering, log-in/out, and refreshing tokens
     */
    .factory('Auth', function($http, $q, store, $timeout, $window, OAuthClientId, OAuthTokens, $rootScope) {
        /**
         * Login a user, storing tokens in localStorage for future usage
         *
         * @param {String} grantType Which OAuth type, `credentials` or `password`?
         * @param {String} username Username
         * @param {String} password Password
         * @return {Object} Promise to deliver token
         */
        function authenticate(grantType, username, password) {
            var payload = {
                grant_type: grantType,
                client_id: OAuthClientId.client_id,
                client_secret: OAuthClientId.client_secret
            };
            if (username) {
                _.assign(payload, {
                    username: username,
                    password: password
                });
            }
            return $http.post(DASHBOARD_API_ROOT + '/token', payload)
                .then(response => {
                    let cyphertext = crypto.encrypt(AES_KEY, JSON.stringify({
                        timestamp: Date.now(),
                        username: username,
                        password: password
                    }));
                    store.set('EV3_RME', cyphertext);
                    return $q.resolve(response);
                })
                .then(handleTokenSuccess.bind(this, grantType), handleTokenError);
        }

        /**
         * Attempt to refresh a token
         *
         * Check localStorage for tokens and expiration times. Always refreshes
         * if possible.
         *
         * @param {String} grantType The original grantType that should be refreshed
         * @return {Object} Promise to deliver token
         */
        function refreshTokens(grantType) {
            var tokens = store.get('tokens.' + grantType);
            OAuthTokens[grantType] = tokens || null;
            var now = Date.now();
            if (!tokens) {
                if (grantType === 'client_credentials')
                    return authenticate.call(this, grantType);
                return $q.reject(new UnauthorizedError());
            }
            if (grantType === 'client_credentials') {
                if (tokens.expiration.access > now)
                    return $q.resolve(tokens);
                return authenticate.call(this, grantType);
            }
            // Both access and refresh token have expired
            if (tokens.expiration.access < now && tokens.expiration.refresh < now) {
                let cyphertext = store.get('EV3_RME');
                let rememberMe = checkRememberMe(cyphertext);
                if (rememberMe !== false)
                    return authenticate.call(this, grantType, rememberMe.username, rememberMe.password);
                store.remove('tokens.' + grantType);
                OAuthTokens[grantType] = null;
                return $q.reject(new UnauthorizedError());
            }

            if (tokens.expiration.access > now)
                return $q.resolve(tokens);

            var payload = {
                client_id: OAuthClientId.client_id,
                client_secret: OAuthClientId.client_secret,
                grant_type: 'refresh_token',
                refresh_token: tokens.refresh_token
            };
            return $http.post(DASHBOARD_API_ROOT + '/token', payload)
                .then(handleTokenSuccess.bind(this, grantType), handleTokenError);
        }

        /**
         * Attempt to decrypt the RemembeMe token
         *
         * @param {String} cyphertext An encrypted username and password
         * @return {Object} Returns false for invalid input or a decrypted set of login credentials
         */
        function checkRememberMe(cyphertext) {
            let payload;
            try {
                // Both JSON and crypto can throw
                payload = crypto.decrypt(AES_KEY, cyphertext);
                payload = JSON.parse(payload);
            } catch (e) {
                store.remove('EV3_RME');
                return false;
            }
            if (Date.now() - payload.timestamp > 14 * 24 * 60 * 60 * 1000) {
                store.remove('EV3_RME');
                return false;
            }
            return payload;
        }

        /**
         * Handle succesful response for /tokens
         *
         * Calculates expiration timestamp and saves tokens to localStorage
         *
         * @param {String} grantType Type of token in httpResponse, used for determining the key under which it'll be stored in localStorage (e.g.: `tokens.password`)
         * @param {Object} httpResponse Raw response
         * @return {Object} Token details
         */
        function handleTokenSuccess(grantType, httpResponse) {
            var tokens = httpResponse.data;
            tokens.expiration = {
                access: Date.now() + parseInt(tokens.expires_in, 10) * 1000 * 0.9 // 0:50 hours
            };
            if (tokens.refresh_token)
                tokens.expiration.refresh = Date.now() + 3 * 55 * 60 * 1000; // 2:45 hours
            store.set('tokens.' + grantType, tokens);
            OAuthTokens[grantType] = tokens;
            return $q.resolve(tokens);
        }

        /**
         * Handle error response for /tokens
         *
         * @param {Object} httpResponse Raw response
         * @throws {Error} Throws the approriate Error based on the HTTP status code
         * @returns {Promise} Failing promise with the correct error
         */
        function handleTokenError(httpResponse) {
            switch (httpResponse.status) {
                case 400:
                    return $q.reject(new BadRequestError(httpResponse));
                case 401:
                    return $q.reject(new UnauthorizedError(httpResponse));
                default:
                    return $q.reject(new HttpError(httpResponse));
            }
        }

        /*
        * Throwable errors
        */
        function UnauthorizedError(response) {
            HttpError.call(this, response);
            this.name = 'Unauthorized';
            this.message = 'Invalid credentials';
        }
        function BadRequestError(response) {
            HttpError.call(this, response);
            this.name = 'BadOAuthRequest';
            this.message = 'Perhaps missing parameters?';
        }
        function HttpError(response) {
            this.name = 'HttpError';
            this.message = 'Request failed';
            _.assign(this, response);
        }
        UnauthorizedError.prototype = new Error();
        BadRequestError.prototype = new Error();
        HttpError.prototype = new Error();

        /**
         * Return object
         *
         * Wraps refresh and auth functions, setting up timeouts for refreshing
         */
        var Auth = {
            /**
             * @ngdoc function
             * @name eventix.common.services.Auth#init
             * @methodOf eventix.common.services.Auth
             * @description
             * Kick off the OAuth logic with a refresh, doesn't set a refresh timer
             *
             * @param {string} grantType Which token to refresh (the original `grantType` used for `authenticate`)
             * @return {Object} Promise to deliver tokens
             */
            init: function(grantType) {
                return refreshTokens.call(this, grantType);
            },
            /**
             * @ngdoc function
             * @name eventix.common.services.Auth#refresh
             * @methodOf eventix.common.services.Auth
             * @description
             * Kick off the OAuth logic with an initial refresh and by setting an
             * interval to refresh 5 minutes before access token expires
             *
             * @param {string} grantType Which token to refresh (the original `grantType` used for `authenticate`)
             * @param {boolean} force Force a refresh even if the old ones haven't expired yet
             * @return {Object} Promise to deliver tokens
             */
            refresh: function(grantType, force = false) {
                if (force) {
                    _.set(OAuthTokens, [grantType, 'expiration', 'access'], 0);
                    store.set('tokens.' + grantType, OAuthTokens[grantType]);
                }
                return refreshTokens.call(this, grantType).then(tokens => {
                    if (this.interval)
                        $timeout.cancel(this.interval);
                    let now = (new Date()).getTime();
                    let refreshTime = tokens.expiration.access - now;
                    this.interval = $timeout(this.refresh.bind(this, grantType), refreshTime);
                    return $q.resolve(tokens);
                });
            },
            /**
             * The placeholder for a $timeout promise for refresh
             */
            interval: undefined,
            /**
             * @ngdoc function
             * @name eventix.common.services.Auth#authenticate
             * @methodOf eventix.common.services.Auth
             * @description
             * Authenticate a user with the API
             *
             * @param {String} grantType Which OAuth type, `credentials` or `password`?
             * @param {String} username Username, optional for `grantType=credentials`
             * @param {String} password Password, optional for `grantType=credentials`
             * @returns {Promise} Returning tokens or auth failure error
             */
            authenticate: function(grantType, username, password) {
                return authenticate.apply(this, arguments).then(tokens => {
                    if (this.interval)
                        $timeout.cancel(this.interval);
                    let now = (new Date()).getTime();
                    let refreshTime = tokens.expiration.access - now;
                    this.interval = $timeout(this.refresh.bind(this, grantType), refreshTime);
                    return $q.resolve(tokens);
                });
            },
            /**
             * @ngdoc function
             * @name eventix.common.services.Auth#logout
             * @methodOf eventix.common.services.Auth
             * @description
             * Remove token from localStorage and this service
             *
             * @param {String} grantType Which grant type? `credentials` or `password`
             */
            logout: function(grantType) {
                store.remove('tokens.' + grantType);
                store.remove('EV3_RME');
                $timeout.cancel(this.interval);
                $rootScope.$broadcast('logout');
                OAuthTokens[grantType] = null;
            },
            /**
             * @ngdoc object
             * @name eventix.common.services.Auth#tokens
             * @propertyOf eventix.common.services.Auth
             * @description
             * A proxy for OAuthTokens
             */
            tokens: OAuthTokens,
            /**
             * @ngdoc method
             * @name eventix.common.services.Auth#stateBeforeLogout
             * @propertyOf eventix.common.services.Auth
             * @description
             * Getter/setter for temporarily saving the state before logout so that it
             * can be restored
             *
             * @param {String} state Name of state
             * @param {Object} params Parameters for state
             * @return {null|Object} If using as getter, it returns object with `state` and `params`
             */
            stateBeforeLogout: (state, params) => {
                if (state && state.length)
                    return store.set('stateBeforeLogout', { state: state, params: params });
                state = store.get('stateBeforeLogout');
                store.remove('stateBeforeLogout');
                return state;
            },
            /**
             * @ngdoc method
             * @name eventix.common.services.Auth#gotoLogin
             * @propertyOf eventix.common.services.Auth
             * @description
             * Goes to the proper login page for this whitelabel
             *
             * @param {StateService} $state We need to inject the stateService because Auth is used in boot phase where $state is unavailable.
             * @return {Any} Null
             */
            gotoLogin: ($state) => {
                var leaveApp = !/^eventix\./.test(LOGIN_PAGE);
                if (leaveApp)
                    return $window.location.href = LOGIN_PAGE;
                return $state.is(LOGIN_PAGE) ? null : $state.go(LOGIN_PAGE, { refresh: true });
            }
        };

        return Auth;
    }).name;
